import React from 'react';
import { 
  Rocket, 
  DollarSign, 
  CheckCircle2, 
  XCircle, 
  Lightbulb, 
  Zap, 
  Target,
  ArrowRight,
  Gift,
  Shield,
  TrendingUp,
  Users
} from 'lucide-react';

const CoOpFundsExplainer = () => {
  const usedForItems = [
    'Done-For-You (DFY) services from our 40+ Virtual Departments',
    'AI Horse-Powered software & tools',
    'Marketing assets and campaigns',
    'Automation builds and workflows',
    'CRM & BDC-X setup',
    'AI agents and support systems',
    'AI compliance and policy installs',
    'Publishing, directory placement, and incentives'
  ];

  const businessStruggles = [
    'Growth tools are expensive',
    'Marketing is risky',
    'Hiring teams is costly',
    'Execution gets delayed'
  ];

  const coopFundsAre = [
    'Issued upfront',
    'Used for real execution, not just tools',
    'Applied automatically when you place POD orders',
    'Designed to grow your business, not create debt',
    'Tracked transparently in your billing dashboard'
  ];

  const coopFundsAreNot = [
    'A loan',
    'A cash payout',
    'Something you have to spend blindly',
    'A coupon with hidden rules'
  ];

  const bigBenefits = [
    'Launch faster',
    'Spend less out of pocket',
    'Avoid hiring multiple vendors',
    'Eliminate wasted trial-and-error',
    'Scale with confidence'
  ];

  return (
    <section className="py-16 md:py-24 bg-gradient-to-b from-slate-900 to-slate-950 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-emerald-500/40 to-transparent" />
        <div className="absolute top-1/4 right-0 w-[500px] h-[500px] bg-emerald-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 left-0 w-[400px] h-[400px] bg-cyan-500/10 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 border border-emerald-500/30 rounded-full px-5 py-2 mb-6">
            <Rocket className="w-5 h-5 text-emerald-400" />
            <span className="text-white font-semibold">Understanding Your Funding</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
            What Are <span className="text-emerald-400">Co-Op Funds</span>?
          </h2>
          
          <p className="text-lg md:text-xl text-slate-300 max-w-3xl mx-auto">
            Co-Op Funds are business growth credits rewarded to you when you become a Channel Partner of AI Horse-Powered Technology.
          </p>
        </div>

        {/* Main Explainer Card */}
        <div className="bg-slate-800/60 border border-slate-700 rounded-2xl p-6 md:p-8 mb-8">
          <p className="text-slate-300 text-lg mb-6">
            They are issued <span className="text-emerald-400 font-semibold">upfront</span> and are designed to help pay for the real work your business needs — <span className="text-cyan-400 font-semibold">not just software</span>.
          </p>
          
          <p className="text-slate-300 mb-4">
            Instead of paying full price out of pocket for marketing, systems, and execution, your Co-Op Funds are applied to:
          </p>
          
          <div className="grid sm:grid-cols-2 gap-3 mb-6">
            {usedForItems.map((item, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                <span className="text-slate-300 text-sm">{item}</span>
              </div>
            ))}
          </div>
          
          <div className="bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border border-emerald-500/30 rounded-xl p-5 text-center">
            <p className="text-white text-lg font-semibold">
              In simple terms: <span className="text-emerald-400">Co-Op Funds reduce or eliminate the cost of getting work done.</span>
            </p>
          </div>
        </div>

        {/* Why Co-Op Funds Exist */}
        <div className="bg-slate-800/60 border border-slate-700 rounded-2xl p-6 md:p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center">
              <Lightbulb className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-white">Why Co-Op Funds Exist</h3>
          </div>
          
          <p className="text-slate-300 mb-4">Most businesses struggle because:</p>
          
          <div className="grid sm:grid-cols-2 gap-3 mb-6">
            {businessStruggles.map((item, idx) => (
              <div key={idx} className="flex items-center gap-3 bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                <XCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                <span className="text-slate-300 text-sm">{item}</span>
              </div>
            ))}
          </div>
          
          <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-5">
            <p className="text-white">
              <span className="text-green-400 font-semibold">Co-Op Funds solve that problem</span> by giving you pre-approved spending power inside a system that already knows how to build and execute for businesses like yours.
            </p>
            <p className="text-slate-300 mt-3">
              You don't just get software. <span className="text-emerald-400 font-semibold">You get a funded execution engine.</span>
            </p>
          </div>
        </div>

        {/* How Co-Op Funds Are Used */}
        <div className="bg-slate-800/60 border border-slate-700 rounded-2xl p-6 md:p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-white">How Co-Op Funds Are Used</h3>
          </div>
          
          <p className="text-slate-300 mb-6">When you place an order with the POD:</p>
          
          <div className="space-y-3 mb-6">
            {[
              'You submit a request (website, ads, automation, etc.)',
              'Our team reviews and sends you a proposal',
              'Your Co-Op Funds are applied to the order value',
              'You only pay the small service or third-party cost (if applicable)',
              'The work begins'
            ].map((step, idx) => (
              <div key={idx} className="flex items-center gap-4 bg-slate-700/50 rounded-xl p-4">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center text-white font-bold flex-shrink-0">
                  {idx + 1}
                </div>
                <span className="text-slate-300">{step}</span>
              </div>
            ))}
          </div>
          
          {/* Example Box */}
          <div className="bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border border-emerald-500/30 rounded-xl p-5">
            <p className="text-emerald-400 font-semibold mb-3">Example:</p>
            <div className="space-y-2 text-slate-300">
              <p>A <span className="text-white font-semibold">$3,000 service order</span></p>
              <p className="flex items-center gap-2">
                <ArrowRight className="w-4 h-4 text-emerald-400" />
                Co-Op Funds cover most or all of the service
              </p>
              <p className="flex items-center gap-2">
                <ArrowRight className="w-4 h-4 text-emerald-400" />
                You pay only the small service fee
              </p>
              <p className="flex items-center gap-2">
                <ArrowRight className="w-4 h-4 text-emerald-400" />
                The remaining amount is deducted from your Co-Op balance
              </p>
            </div>
            <p className="text-green-400 font-semibold mt-4">
              You just saved thousands while still getting professional execution.
            </p>
          </div>
        </div>

        {/* What Makes Co-Op Funds Different */}
        <div className="bg-slate-800/60 border border-slate-700 rounded-2xl p-6 md:p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-violet-600 flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-white">What Makes Co-Op Funds Different</h3>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* They ARE */}
            <div>
              <p className="text-green-400 font-semibold mb-3">Co-Op Funds ARE:</p>
              <div className="space-y-2">
                {coopFundsAre.map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-300 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* They are NOT */}
            <div>
              <p className="text-red-400 font-semibold mb-3">They are NOT:</p>
              <div className="space-y-2">
                {coopFundsAreNot.map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-300 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <p className="text-slate-400 text-center mt-6">
            They are a <span className="text-cyan-400 font-semibold">growth credit system</span> tied to your success inside the platform.
          </p>
        </div>

        {/* The Big Benefit */}
        <div className="bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 border border-emerald-500/30 rounded-2xl p-6 md:p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-white">The Big Benefit for You</h3>
          </div>
          
          <p className="text-slate-300 mb-4">With Co-Op Funds, you can:</p>
          
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3 mb-6">
            {bigBenefits.map((item, idx) => (
              <div key={idx} className="flex items-center gap-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                <span className="text-white text-sm font-medium">{item}</span>
              </div>
            ))}
          </div>
          
          <div className="bg-slate-900/50 rounded-xl p-5 text-center">
            <p className="text-slate-300 mb-2">You're no longer choosing between:</p>
            <p className="text-slate-400 italic mb-2">"Do I pay for tools?" or "Do I pay for help?"</p>
            <p className="text-emerald-400 font-semibold">You get both — backed by Co-Op support.</p>
          </div>
        </div>

        {/* Final Summary */}
        <div className="bg-gradient-to-r from-cyan-500/20 to-emerald-500/20 border border-cyan-500/30 rounded-2xl p-6 md:p-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Target className="w-8 h-8 text-cyan-400" />
          </div>
          <h3 className="text-xl md:text-2xl font-bold text-white mb-4">In One Sentence</h3>
          <p className="text-2xl md:text-3xl font-bold text-emerald-400 mb-4">
            Co-Op Funds turn your membership into funded execution.
          </p>
          <p className="text-slate-300 text-lg">
            Instead of just giving you access to technology, AI Horse-Powered Technology gives you the ability to <span className="text-cyan-400 font-semibold">use it at scale</span> — without carrying the full cost yourself.
          </p>
        </div>
      </div>
    </section>
  );
};

export default CoOpFundsExplainer;
